/**
 * Account API 모듈
 *
 * 사용자(Account) 관련 API 요청 함수들을 제공합니다.
 * - 모든 함수는 apiClient를 통해 인증 토큰이 자동으로 포함됩니다.
 * - 에러 발생 시 호출부에서 처리할 수 있도록 throw합니다.
 */

import type {
  AccountDetailDto,
  GetAccountEnrollmentsResponse,
  GetAccountsParams,
  PaginatedResponseDto,
  AccountResponseDto,
  ResetAdminPasswordDto,
  UpdateAccountDto,
} from '@repo/types';
import type { AccountKind } from '@theailab-dev/parchment';
import apiClient from '../lib/apiClient';

/**
 * 사용자 목록 조회
 *
 * 흐름:
 * 1. 페이지네이션 파라미터(page) 준비
 * 2. 필터링 조건이 있으면 filterKey, filterValue 추가
 * 3. GET /account 요청
 * 4. 응답 데이터 반환 (data: Account[], meta: PaginationMeta)
 *
 * @param params - 조회 옵션
 * @param params.page - 페이지 번호 (1부터 시작)
 * @param params.filterKey - 필터 키 (예: "state", "kind", "search")
 * @param params.filterValue - 필터 값 (예: "NORMAL", "USER", "검색어")
 * @returns 사용자 목록과 페이지네이션 메타데이터
 * @throws API 요청 실패 시 에러
 */
export const getAccounts = async (params: GetAccountsParams): Promise<PaginatedResponseDto<AccountResponseDto>> => {
  try {
    console.log('🔍 사용자 목록 조회 요청:', params);

    // 기본 요청 파라미터 설정 (페이지 번호는 필수)
    const requestParams: Record<string, string | number> = {
      page: params.page ?? 1,
    };

    if (params.limit !== undefined) {
      requestParams.limit = params.limit;
    }

    // 필터링 조건이 있으면 추가
    // filterKey: 필터 대상 필드 (state, kind, search 등)
    // filterValue: 필터 값 (NORMAL, USER, 검색어 등)
    if (params.filterKey && params.filterValue) {
      requestParams.filterKey = params.filterKey;
      requestParams.filterValue = params.filterValue;
    }

    // API 요청: GET /account?page=1&filterKey=state&filterValue=NORMAL
    const response = await apiClient.get<PaginatedResponseDto<AccountResponseDto>>('/account', {
      params: requestParams,
    });

    console.log('✅ 사용자 목록 조회 성공:', response.data);
    return response.data;
  } catch (error) {
    // 에러 로깅 후 호출부로 전달 (react-query에서 처리)
    console.error('❌ 사용자 목록 조회 실패:', error);
    throw error;
  }
};

/**
 * 사용자 상세 정보 조회
 *
 * 흐름:
 * 1. accountId를 URL 파라미터로 사용
 * 2. GET /account/{accountId} 요청
 * 3. 사용자 상세 정보 반환 (기본 정보 + 소셜 계정 + 좋아요 + 수강 정보 첫 5개)
 *
 * @param accountId - 조회할 사용자 ID
 * @returns AccountDetailDto - packages/types에 정의된 사용자 상세 정보
 *   - 기본 정보: id, email, name, phone, state, kind 등
 *   - 인증 정보: emailCertifiedAt, phoneCertifiedAt 등
 *   - 관계 데이터: socialAccounts, accountLikes, enrollments (최대 5개)
 *   - enrollmentMeta: 수강 정보 전체 개수 및 페이지 수
 * @throws API 요청 실패 시 에러 (404: 사용자 없음, 401: 인증 실패 등)
 */
export const getAccountDetail = async (accountId: number): Promise<AccountDetailDto> => {
  try {
    console.log('🔍 사용자 상세 조회 요청:', accountId);

    // API 요청: GET /account/{accountId}
    // packages/types의 AccountDetailDto 타입 사용으로 타입 안전성 보장
    // 수강 정보는 최대 5개만 포함 (1페이지)
    const response = await apiClient.get<AccountDetailDto>(`/account/${accountId}`);

    console.log('✅ 사용자 상세 조회 성공:', response.data);
    return response.data;
  } catch (error) {
    // 에러 로깅 후 호출부로 전달 (react-query에서 처리)
    console.error('❌ 사용자 상세 조회 실패:', error);
    throw error;
  }
};

/**
 * 사용자 수강 목록 추가 조회 (더보기용)
 *
 * 흐름:
 * 1. accountId와 페이지 파라미터로 요청
 * 2. GET /account/{accountId}/enrollments?page=N&limit=5 요청
 * 3. 해당 페이지의 수강 정보 목록 반환
 *
 * @param accountId - 조회할 사용자 ID
 * @param page - 페이지 번호 (기본값: 2, 1페이지는 상세 조회 시 이미 받음)
 * @param limit - 페이지당 개수 (기본값: 5)
 * @returns GetAccountEnrollmentsResponse - 수강 목록 및 페이지네이션 정보
 *   - data: AccountEnrollmentListItemDto[] - 수강 정보 배열
 *   - total: 전체 개수
 *   - page: 현재 페이지
 *   - limit: 페이지당 개수
 *   - totalPages: 전체 페이지 수
 * @throws API 요청 실패 시 에러
 */
export const getAccountEnrollments = async (
  accountId: number,
  page: number = 2,
  limit: number = 5,
): Promise<GetAccountEnrollmentsResponse> => {
  try {
    console.log('🔍 수강 목록 추가 조회 요청:', { accountId, page, limit });

    // API 요청: GET /account/{accountId}/enrollments?page=2&limit=5
    const response = await apiClient.get<GetAccountEnrollmentsResponse>(`/account/${accountId}/enrollments`, {
      params: { page, limit },
    });

    console.log('✅ 수강 목록 추가 조회 성공:', response.data);
    return response.data;
  } catch (error) {
    // 에러 로깅 후 호출부로 전달
    console.error('❌ 수강 목록 추가 조회 실패:', error);
    throw error;
  }
};

/**
 * 사용자 비밀번호 초기화
 *
 * 흐름:
 * 1. accountId를 URL 파라미터로 사용
 * 2. POST /account/{accountId}/reset-password 요청
 * 3. 서버에서 임시 비밀번호 생성 후 사용자 이메일로 전송
 * 4. 성공 응답 반환
 *
 * @param accountId - 비밀번호를 초기화할 사용자 ID
 * @returns 성공 메시지 또는 응답 데이터
 * @throws API 요청 실패 시 에러 (404: 사용자 없음, 401: 권한 없음 등)
 */
export const resetAccountPassword = async (accountId: number): Promise<{ message: string }> => {
  try {
    console.log('🔐 비밀번호 초기화 요청:', accountId);

    // API 요청: POST /account/{accountId}/reset-password
    // 메일 발송이 시간이 걸릴 수 있으므로 타임아웃을 90초로 설정
    const response = await apiClient.post<{ message: string }>(
      `/account/${accountId}/reset-password`,
      {},
      {
        timeout: 90000, // 90초 타임아웃 (메일 발송 대기)
      },
    );

    console.log('✅ 비밀번호 초기화 성공:', response.data);
    return response.data;
  } catch (error) {
    // 에러 로깅 후 호출부로 전달
    console.error('❌ 비밀번호 초기화 실패:', error);
    throw error;
  }
};

/**
 * 관리자 비밀번호 변경
 *
 * 흐름:
 * 1. 현재 비밀번호와 새 비밀번호를 body에 포함
 * 2. POST /account/admin/reset-password 요청
 * 3. 서버에서 현재 비밀번호 검증 후 새 비밀번호로 변경
 * 4. 성공 응답 반환
 *
 * @param data - 비밀번호 변경 데이터
 * @param data.currentPassword - 현재 비밀번호
 * @param data.newPassword - 새 비밀번호 (최소 8자 이상)
 * @returns 성공 메시지 또는 응답 데이터
 * @throws API 요청 실패 시 에러 (401: 현재 비밀번호 불일치, 400: 유효성 검증 실패 등)
 */
export const changeAdminPassword = async (
  data: ResetAdminPasswordDto,
): Promise<{ success: boolean; message?: string }> => {
  try {
    console.log('🔐 관리자 비밀번호 변경 요청');

    // API 요청: POST /account/admin/reset-password
    const response = await apiClient.post<{
      success: boolean;
      message?: string;
    }>('/account/admin/reset-password', {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });

    console.log('✅ 관리자 비밀번호 변경 성공:', response.data);
    return response.data;
  } catch (error) {
    // 에러 로깅 후 호출부로 전달
    console.error('❌ 관리자 비밀번호 변경 실패:', error);
    throw error;
  }
};

/**
 * 계정 정보 업데이트 (현재는 kind만 변경 가능)
 *
 * 엔드포인트: PATCH /api/account/:id
 * - 관리자 권한 필요 (staff.kind === ACCOUNT_KIND.ADMIN)
 * - 현재는 kind 필드만 변경 가능하지만, 향후 확장 가능
 *
 * 흐름:
 * 1. accountId와 새 유형(kind)을 body에 포함 { kind: AccountKind }
 * 2. PATCH /account/{accountId} 요청
 * 3. 서버에서 사용자 유형 변경
 * 4. 업데이트된 AccountResponseDto 반환
 *
 * @param accountId - 계정 ID
 * @param kind - 변경할 계정 유형 (AccountKind)
 * @returns 업데이트된 계정 정보 (AccountResponseDto)
 * @throws API 요청 실패 시 에러 (404: 사용자 없음, 403: 권한 없음, 400: 유효성 검증 실패 등)
 */
export const changeAccountKind = async (accountId: number, kind: AccountKind): Promise<AccountResponseDto> => {
  try {
    console.log('🔄 사용자 유형 변경 요청:', { accountId, kind });

    // API 요청: PATCH /account/{accountId}
    const dto: UpdateAccountDto = { kind };
    const response = await apiClient.patch<AccountResponseDto>(`/account/${accountId}`, dto);

    console.log('✅ 사용자 유형 변경 성공:', response.data);
    return response.data;
  } catch (error) {
    // 에러 로깅 후 호출부로 전달
    console.error('❌ 사용자 유형 변경 실패:', error);
    throw error;
  }
};

/**
 * 계정 탈퇴 처리 (관리자에 의한 강제 탈퇴)
 *
 * 흐름:
 * 1. accountId를 URL 파라미터로 사용
 * 2. POST /account/{accountId}/delete 요청
 * 3. 서버에서 계정 정보 마스킹 및 상태 변경 (DELETED)
 * 4. 성공 응답 반환
 *
 * @param accountId - 탈퇴 처리할 사용자 ID
 * @returns 업데이트된 계정 정보 (AccountDetailDto)
 * @throws API 요청 실패 시 에러
 */
export const deleteAccount = async (accountId: number): Promise<AccountDetailDto> => {
  try {
    console.log('🗑️ 계정 탈퇴 처리 요청:', accountId);

    // API 요청: POST /account/{accountId}/delete
    const response = await apiClient.post<AccountDetailDto>(`/account/${accountId}/delete`);

    console.log('✅ 계정 탈퇴 처리 성공:', response.data);
    return response.data;
  } catch (error) {
    // 에러 로깅 후 호출부로 전달
    console.error('❌ 계정 탈퇴 처리 실패:', error);
    throw error;
  }
};
